import { useTranslation } from "react-i18next";
import { ERROR_CODES } from "@nh/shared";
import { isApiError } from "@/shared/api/client";

/**
 * El texto que se le enseña a una persona cuando el API falla.
 *
 * Se ramifica sobre `code`, NUNCA sobre el texto del mensaje: el mensaje
 * del servidor puede cambiar de redacción o venir en otro idioma, y un
 * `if (message.includes("no encontrado"))` se rompe sin que nada lo avise.
 *
 * Para lo que no tiene un texto propio se usa el mensaje del servidor —ya
 * viene redactado para leerse— y solo si tampoco hay, un genérico. Un
 * "algo salió mal" tapando un "ese slug ya existe" es lo que hace que
 * alguien reintente diez veces lo mismo.
 */
export function useApiErrorMessage(error: unknown): string {
  const { t } = useTranslation();
  if (!error) return "";

  if (!isApiError(error)) {
    return t("Algo salió mal. Vuelve a intentarlo.");
  }

  switch (error.code) {
    case ERROR_CODES.FORBIDDEN:
      return t("Tu cuenta no tiene permiso para esta acción.");
    case ERROR_CODES.UNAUTHENTICATED:
      return t("Tu sesión expiró. Vuelve a entrar.");
    case ERROR_CODES.NOT_FOUND:
      return t("Ese producto ya no existe.");
    case ERROR_CODES.CONFLICT:
      return t("Ya existe un producto con ese slug.");
    case ERROR_CODES.INTERNAL:
      return error.message || t("Algo salió mal. Vuelve a intentarlo.");
    default:
      return error.message || t("Algo salió mal. Vuelve a intentarlo.");
  }
}
